import { Injectable } from "@angular/core";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { Subject, Observable, BehaviorSubject, Subscription, take } from "rxjs";
import { environment } from "src/environments/environment";
import { NotificationsToastService } from "./Notifications-Toast.service";
import { AzureBotService } from "../../ui/organisms/azure-bot/services/AzureBot.service";
import { SignalUserAPIService } from "./API/Signal-UserAPI.service";
import { SignalUser } from "../models/SignalUser/SignalUser";

const SERVER_TIMEOUT_MS = 60000;
const KEEP_ALIVE_INTERVAL_MS = 10000;

@Injectable({
  providedIn: "root",
})
export class SignalRService {
  public signalConnection: HubConnection;
  public ConnectionId: string;
  private connectionIdSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public connectionId$: Observable<string | null> = this.connectionIdSubject.asObservable();
  $dataGeoJson: any;
  incommingsConfigUrl: any;
  onUpdateLocationActive$: Subject<string> = new Subject<string>();

  private registeredIdentifiers: Set<string> = new Set<string>();
  
  private locationIdentifier: string = null;

  constructor(
    private notificationsToastService: NotificationsToastService,
    private AzureBotService: AzureBotService,
    private signalUserAPIService: SignalUserAPIService
  ) {
    this.incommingsConfigUrl = environment.signalRHost;
  }

  public async startConnection(): Promise<void> {
    if (this.signalConnection) {
      const state = this.signalConnection.state;
      if ( state === HubConnectionState.Connected || state === HubConnectionState.Connecting || state === HubConnectionState.Reconnecting ) {
        if (!environment.production) console.log("SignalR ya tiene una conexión activa, estado:", state);
        return;
      }

      try {
        await this.signalConnection.stop();
      } catch (err) {
        if (!environment.production) console.warn("Error cerrando la conexión anterior de SignalR:", err);
      }
      this.signalConnection = null;
    }

    this.signalConnection = new HubConnectionBuilder()
      .withUrl(this.incommingsConfigUrl + "/Signal")
      .withAutomaticReconnect()
      .withServerTimeout(SERVER_TIMEOUT_MS)
      .withKeepAliveInterval(KEEP_ALIVE_INTERVAL_MS)
      .configureLogging(environment.production ? LogLevel.None : LogLevel.Warning)
      .build();

    this.signalConnection.onreconnected(async (connectionId) => {
      if (!environment.production) console.log("SignalR reconectado, connectionId:", connectionId);
      await this.getConnectionId();
      this.resubscribeIdentifiers();
    });

    this.signalConnection.onreconnecting((error) => {
      if(!environment.production) console.warn("SignalR reconectando...", error);
    });

    this.signalConnection.onclose((error) => {
      if(!environment.production) console.warn("SignalR conexión cerrada", error);
    });

    try {
      await this.signalConnection.start();
      if (!environment.production) console.log("Conexión iniciada");
      await this.getConnectionId();
      this.TestListener();
    } catch (err) {
      console.error("Error comenzando conexión de SignalR:", err);
    }
  }

  public async endConnection(): Promise<void> {
    if (!this.signalConnection) {
      return;
    }

    this.CloseAllListeners();
    this.registeredIdentifiers.clear();
    this.locationIdentifier = null;

    try {
      await this.signalConnection.stop();
      if (!environment.production) console.log("Conexión terminada");
    } catch (err) {
      console.error("Error terminando conexión de SignalR:", err);
    } finally {
      this.signalConnection = null;
      this.ConnectionId = null;
      this.connectionIdSubject.next(null);
    }
  }

  private async getConnectionId(): Promise<string | null> {
    if (!this.signalConnection) {
      return null;
    }

    try {
      const data = await this.signalConnection.invoke("GetConnectionId");
      if (!environment.production) console.log("Connection Id: " + data);
      this.ConnectionId = data;
      this.connectionIdSubject.next(this.ConnectionId);
      return this.ConnectionId;
    } catch (err) {
      console.error("Error obteniendo el ConnectionId de SignalR:", err);
      return null;
    }
  }

  public async waitForConnectionId(timeoutMs: number = 10000): Promise<string> {
    if (this.ConnectionId) {
      return this.ConnectionId;
    }

    return new Promise((resolve, reject) => {
      let settled = false;
      let timeoutHandle: any = null;
      let subscription: Subscription = null;

      const finish = (connectionId: string) => {
        settled = true;
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
        if (subscription) {
          subscription.unsubscribe();
        }
        resolve(connectionId);
      };

      subscription = this.connectionId$.subscribe((connectionId) => {
        if (connectionId && !settled) {
          finish(connectionId);
        }
      });

      if (settled) {
        subscription.unsubscribe();
        return;
      }

      timeoutHandle = setTimeout(() => {
        settled = true;
        subscription.unsubscribe();
        reject(new Error("Timeout esperando ConnectionId"));
      }, timeoutMs);
    });
  }

  registerIdentifier(identifier: string): void {
    if (identifier) {
      this.registeredIdentifiers.add(identifier);
    }
  }

  unregisterIdentifier(identifier: string): void {
    if (identifier) {
      this.registeredIdentifiers.delete(identifier);
    }
  }

  resubscribeIdentifiers(): void {
    if (!this.ConnectionId || this.registeredIdentifiers.size === 0) {
      return;
    }

    this.registeredIdentifiers.forEach((identifier) => {
      const signalUser = new SignalUser();
      signalUser.ConnectionId = this.ConnectionId;
      signalUser.Identifier = identifier;

      this.signalUserAPIService
        .AddSignalUser(signalUser)
        .pipe(take(1))
        .subscribe({
          next: () => {
            if (!environment.production) console.log("Suscripción de SignalR restablecida para: " + identifier);
          },
          error: (error) => {
            console.error("Error restableciendo la suscripción de SignalR para: " + identifier, error);
          },
        });
    });
  }

  //accion de nueva posicion de un vehiculo
  public ActivateListenerUpdateLocationActive (identifier?: string) : any {
    if (!this.signalConnection) {
      console.warn('ActivateListenerUpdateLocationActive: signalConnection no está inicializada');
      return;
    }
    this.unregisterIdentifier(this.locationIdentifier);
    this.locationIdentifier = identifier ? identifier : null;
    this.registerIdentifier(this.locationIdentifier);
    this.signalConnection.off("UpdateLocationActive");

    this.signalConnection.on("UpdateLocationActive", (data) => {
      if(!environment.production) console.log("Mensaje SIGNAL RECIBIDO *****************,"+data);
      this.setOnUpdateLocationActive(data);
    });
  };

  public DeactivateListenerUpdateLocationActive (identifier?: string) : any {
    this.unregisterIdentifier(identifier ? identifier : this.locationIdentifier);
    this.locationIdentifier = null;
    if (!this.signalConnection) {
      console.warn('DeactivateListenerUpdateLocationActive: signalConnection no está inicializada');
      return;
    }
    this.signalConnection.off("UpdateLocationActive");
    if(!environment.production) console.log("Listener UpdateLocationActive desactivado");
  };

  //accion de nueva notificación, activa toasts
  public ActivateListenerNewNotification () : any {
    if (!this.signalConnection) {
      console.warn('ActivateListenerNewNotification: signalConnection no está inicializada');
      return;
    }
    this.registerIdentifier("General");
    this.signalConnection.off("NewNotification");

    this.signalConnection.on("NewNotification", (data) => {
      var notificationDTO = JSON.parse(data);

      if(!environment.production) console.log("Notificación SIGNAL RECIBIDA *****************,"+notificationDTO, data);
      this.notificationsToastService.showCustomToast(notificationDTO);
    });
  };

  public DeactivateListenerNewNotification () : any {
    this.unregisterIdentifier("General");
    if (!this.signalConnection) {
      console.warn('DeactivateListenerNewNotification: signalConnection no está inicializada');
      return;
    }
    this.signalConnection.off("NewNotification");
    if(!environment.production) console.log("Listener NewNotification desactivado");
  };

  //accion de asignación
  public ActivateListenerAssignment () : any {
    if (!this.signalConnection) {
      console.warn('ActivateListenerAssignment: signalConnection no está inicializada');
      return;
    }
    this.signalConnection.off("Assignment");

    this.signalConnection.on("Assignment", (data) => {
      var assignmentDTO = JSON.parse(data);

      if(!environment.production) console.log("Asignación SIGNAL RECIBIDA *****************,"+assignmentDTO, data);
      this.notificationsToastService.showCustomToast(assignmentDTO);
    });
  };

  public DeactivateListenerAssignment () : any {
    if (!this.signalConnection) {
      console.warn('DeactivateListenerAssignment: signalConnection no está inicializada');
      return;
    }
    this.signalConnection.off("Assignment");
    if(!environment.production) console.log("Listener Assignment desactivado");
  };

   public TestListener () : any {
    if (!this.signalConnection) {
      console.warn('TestListener called but signalConnection is not initialized yet');
      return;
    }

    console.log('Registering TestListener for TestSignalMessage');
    this.signalConnection.off("TestSignalMessage");
    this.signalConnection.on("TestSignalMessage", (data) => {
      let assignmentDTO: any = data;
      try {
        if (typeof data === 'string') {
          assignmentDTO = JSON.parse(data);
        }
      } catch (err) {
        console.warn('Error parseando TestSignalMessage', err);
        assignmentDTO = data;
      }

      console.log("SEÑAL RECIBIDA *****************", assignmentDTO, data);
      try {
        this.notificationsToastService.showCustomToast(data);
      } catch (e) {
        console.error('Error en TestSignalMessage', e);
      }
    });
  };

  public DeactivateTestListener () : any {
    if (!this.signalConnection) {
      console.warn('DeactivateTestListener: signalConnection no está inicializada');
      return;
    }
    this.signalConnection.off("TestSignalMessage");
    console.log("Listener TestSignalMessage desactivado");
  };

  //acciones del bot
  public ActivateListenerChatBot () : any {
    if (!this.signalConnection) {
      console.warn('ActivateListenerChatBot: signalConnection no está inicializada');
      return;
    }
    this.registerIdentifier(this.AzureBotService.signalIdentifier);
    this.signalConnection.off("ChatBotAction");

    this.signalConnection.on("ChatBotAction", (data) => {
      if(!environment.production) console.log("Mensaje SIGNAL RECIBIDO *****************,"+data);
      this.AzureBotService.startAction(data);
    });
  };

  public DeactivateListenerChatBot () : any {
    this.unregisterIdentifier(this.AzureBotService.signalIdentifier);
    if (!this.signalConnection) {
      console.warn('DeactivateListenerChatBot: signalConnection no está inicializada');
      return;
    }
    this.signalConnection.off("ChatBotAction");
    if(!environment.production) console.log("Listener ChatBotAction desactivado");
  };

  public getOnUpdateLocationActive(): Observable<string>{
    return this.onUpdateLocationActive$.asObservable();
  }

  public setOnUpdateLocationActive(data): any{
    return this.onUpdateLocationActive$.next(data);
  }

  public CloseAllListeners() : any {
    this.DeactivateListenerUpdateLocationActive();
    this.DeactivateListenerNewNotification();
    this.DeactivateListenerAssignment();
    this.DeactivateListenerChatBot();
    this.DeactivateTestListener();
  }
}
